import {
  type AccountClass,
  collapseParentRollupPeriods,
  type ParentRollup,
  reportingKeys,
} from '@taler/api-client'
import type { CurrencyCode, Money } from '@taler/domain'
import { useQueries } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { ACCOUNT_CLASSES } from '../internal/account-form.ts'

export interface AccountBalance {
  readonly own: Money
  readonly rolledUp: Money
}

export interface UseAccountBalancesOptions {
  readonly ledgerId: () => string | undefined
  readonly displayCurrency: () => CurrencyCode
  readonly until: () => string
  readonly enabled: () => boolean
}

export interface UseAccountBalances {
  readonly byAccount: ComputedRef<ReadonlyMap<string, AccountBalance>>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
}

/** One `reporting_parentRollup` call per class, batched; `collapseParentRollupPeriods` folds the periods into one snapshot. */
export function useAccountBalances(options: UseAccountBalancesOptions): UseAccountBalances {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const results = useQueries({
    queries: computed(() =>
      (ACCOUNT_CLASSES as readonly AccountClass[]).map((accountClass) => {
        const query = {
          class: accountClass,
          bucket: 'YEAR' as const,
          until: options.until(),
          displayCurrency: options.displayCurrency(),
        }
        return {
          queryKey: reportingKeys.parentRollup(options.ledgerId() ?? '', query),
          queryFn: ({ signal }: { signal?: AbortSignal }) =>
            api.reporting.parentRollup(options.ledgerId()!, query, signal),
          enabled: active.value,
          retry: false,
        }
      }),
    ),
  })

  const byAccount = computed(() => {
    const out = new Map<string, AccountBalance>()
    for (const result of results.value) {
      const data = result.data as ParentRollup | undefined
      if (data === undefined) continue
      for (const row of collapseParentRollupPeriods(data).rows)
        out.set(row.accountId, { own: row.ownBalance, rolledUp: row.rolledUpBalance })
    }
    return out
  })

  return {
    byAccount,
    isLoading: computed(() => active.value && results.value.some((result) => result.isPending)),
    error: computed(() => results.value.find((result) => result.error)?.error ?? null),
  }
}

/** An account whose balance is read in its own currency; `currency` is the account's home currency. */
export interface NativeBalanceAccount {
  readonly id: string
  readonly accountClass: AccountClass
  readonly currency: CurrencyCode
}

export interface UseNativeAccountBalancesOptions {
  readonly ledgerId: () => string | undefined
  readonly accounts: () => readonly NativeBalanceAccount[]
  readonly until: () => string
  readonly enabled: () => boolean
}

export interface UseNativeAccountBalances {
  readonly byAccount: ComputedRef<ReadonlyMap<string, Money>>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
}

/** Each account's own-currency balance: one query per (class, currency) pair, read at that currency as display. */
export function useNativeAccountBalances(options: UseNativeAccountBalancesOptions): UseNativeAccountBalances {
  const api = useApi()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.accounts().length > 0,
  )

  const groups = computed(() => {
    const out = new Map<string, { accountClass: AccountClass; currency: CurrencyCode; ids: Set<string> }>()
    for (const account of options.accounts()) {
      const key = `${account.accountClass}:${account.currency}`
      const group = out.get(key) ?? {
        accountClass: account.accountClass,
        currency: account.currency,
        ids: new Set(),
      }
      group.ids.add(account.id)
      out.set(key, group)
    }
    return [...out.values()]
  })

  const results = useQueries({
    queries: computed(() =>
      groups.value.map(({ accountClass, currency }) => {
        const query = {
          class: accountClass,
          bucket: 'YEAR' as const,
          until: options.until(),
          displayCurrency: currency,
        }
        return {
          queryKey: reportingKeys.parentRollup(options.ledgerId() ?? '', query),
          queryFn: ({ signal }: { signal?: AbortSignal }) =>
            api.reporting.parentRollup(options.ledgerId()!, query, signal),
          enabled: active.value,
          retry: false,
        }
      }),
    ),
  })

  const byAccount = computed(() => {
    const out = new Map<string, Money>()
    groups.value.forEach(({ ids }, index) => {
      const data = results.value[index]?.data as ParentRollup | undefined
      if (data === undefined) return
      for (const row of collapseParentRollupPeriods(data).rows)
        if (ids.has(row.accountId)) out.set(row.accountId, row.ownBalance)
    })
    return out
  })

  return {
    byAccount,
    isLoading: computed(() => active.value && results.value.some((result) => result.isPending)),
    error: computed(() => results.value.find((result) => result.error)?.error ?? null),
  }
}
